import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import KeyInventory from './pages/KeyInventory';
import Locations from './pages/Locations';
import Users from './pages/Users';
import People from './pages/People';
import KeyTypes from './pages/KeyTypes';
import Settings from './pages/Settings';
import BackupRestore from './pages/BackupRestore';
import Help from './pages/Help';
import Login from './pages/Login';
import Logout from './pages/Logout';
import MyKeys from './pages/MyKeys';
// Export functionality moved to BackupRestore page
import NotificationSettings from './pages/NotificationSettings';
import Visitors from './pages/Visitors';
import KeyRequests from './pages/KeyRequests';
import KeyTypeDetail from './pages/KeyTypeDetail';
import { initializeData } from './utils/seedData';
import { store } from './store';
import { authApi, AuthUser } from './services/api';
import { setCurrentUser } from './store/usersSlice';

function LoadingSpinner({ message = 'Loading KeyKeeper...' }: { message?: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">{message}</p>
      </div>
    </div>
  );
}

function ErrorScreen({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center max-w-md px-4">
        <div className="text-red-500 text-5xl mb-4">!</div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Connection Error</h1>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={onRetry}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Retry Connection
        </button>
      </div>
    </div>
  );
}

function UnauthorizedScreen({ authUser }: { authUser: AuthUser }) {
  const handleRegister = async () => {
    try {
      await authApi.register({ name: authUser.name });
      window.location.reload();
    } catch (error) {
      console.error('Registration failed:', error);
      alert('Failed to complete registration. Please contact an administrator.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center max-w-md px-4">
        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Welcome to KeyKeeper</h1>
        <p className="text-gray-600 mb-2">You're signed in as:</p>
        <p className="font-medium text-gray-900 mb-1">{authUser.name}</p>
        <p className="text-sm text-gray-500 mb-6">{authUser.email}</p>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-800">
            Your account needs to be activated by an administrator before you can access KeyKeeper.
          </p>
        </div>

        <button
          onClick={handleRegister}
          className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors mb-3"
        >
          Request Access
        </button>

        <a
          href={authApi.getLogoutUrl()}
          className="block text-sm text-gray-500 hover:text-gray-700"
        >
          Sign out and use a different account
        </a>
      </div>
    </div>
  );
}

function AccessDeniedScreen() {
  const [isPromoting, setIsPromoting] = useState(false);
  const [promoteError, setPromoteError] = useState<string | null>(null);

  const handlePromote = async () => {
    setIsPromoting(true);
    setPromoteError(null);
    try {
      await authApi.promoteToAdmin();
      // Reload the page to refresh auth state
      window.location.reload();
    } catch (error) {
      console.error('Promotion failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to promote to admin';
      setPromoteError(errorMessage);
      setIsPromoting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center max-w-md px-4">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Access Pending</h1>
        <p className="text-gray-600 mb-6">
          Your access request has been submitted. Please wait for an administrator to approve your account.
        </p>
        
        <div className="mb-4">
          <p className="text-sm text-secondary-600 mb-3">
            Are you the first user? Promote yourself to admin to get started.
          </p>
          <button
            onClick={handlePromote}
            disabled={isPromoting}
            className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-3"
          >
            {isPromoting ? 'Promoting...' : 'Promote to Admin'}
          </button>
          {promoteError && (
            <p className="text-sm text-red-600 mb-3">{promoteError}</p>
          )}
        </div>

        <a
          href={authApi.getLogoutUrl()}
          className="inline-block px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Sign Out
        </a>
      </div>
    </div>
  );
}

export default function App() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authState, setAuthState] = useState<'checking' | 'authenticated' | 'not_authenticated' | 'new_user' | 'pending'>('checking');
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);

  const initialize = async () => {
    setLoading(true);
    setError(null);
    setAuthState('checking');

    try {
      // Check if we're in development mode (no auth)
      const isDev = import.meta.env.DEV;

      if (isDev) {
        // In development, skip auth check and use first admin user
        await initializeData(store);
        setAuthState('authenticated');
        setLoading(false);
        return;
      }

      // In production, check authentication
      try {
        const currentUser = await authApi.getCurrentUser();

        if (currentUser.isNewUser) {
          // New user, needs to register
          setAuthUser(currentUser);
          setAuthState('new_user');
          setLoading(false);
          return;
        }

        // User exists and is authenticated
        setAuthUser(currentUser);

        // Check if user has any role assigned (not just 'user' with no access)
        if (currentUser.role === 'user' && (!currentUser.locationAccess || currentUser.locationAccess.length === 0)) {
          // User registered but not yet granted access
          setAuthState('pending');
          setLoading(false);
          return;
        }

        // Set the current user in Redux
        store.dispatch(setCurrentUser(currentUser));

        // Initialize app data
        await initializeData(store);
        setAuthState('authenticated');
        
        // Clean up browser history - replace current entry to prevent back button issues
        // This removes any login redirects from history so back button works naturally
        const currentPath = window.location.pathname;
        if (currentPath === '/login' || currentPath === '/') {
          window.history.replaceState({}, '', '/');
        }
        
        setLoading(false);
      } catch (authError) {
        // Check if this is an authentication error (401/403) vs server error (500)
        const errorMessage = authError instanceof Error ? authError.message : String(authError);
        console.error('Auth check failed:', authError);

        // Only show login page if it's an actual authentication error
        if (errorMessage.includes('HTTP 401') || errorMessage.includes('HTTP 403') || 
            errorMessage.includes('Authentication required') || errorMessage.includes('Not authenticated')) {
          // User is not authenticated - show login page instead of redirecting
          setAuthState('not_authenticated');
          // Clean up history - replace with /login so back button doesn't cause loops
          window.history.replaceState({}, '', '/login');
          setLoading(false);
        } else {
          // Server error, database issue, or network problem - don't redirect
          // Let the outer catch handle it with retry option
          throw authError;
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect to server');
      setLoading(false);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  // Re-check authentication after Azure AD redirect
  useEffect(() => {
    // If we're not authenticated but not on login/logout pages, 
    // we might be coming back from Azure AD auth - re-check
    if (authState === 'not_authenticated') {
      const pathname = window.location.pathname;
      if (pathname !== '/login' && pathname !== '/logout') {
        // Re-initialize to check if user is now authenticated after redirect
        const timer = setTimeout(() => {
          initialize();
        }, 500); // Longer delay to allow Azure Static Web Apps to process auth
        return () => clearTimeout(timer);
      }
    }
  }, [authState]);

  if (loading) {
    return <LoadingSpinner message={authState === 'checking' ? 'Checking authentication...' : 'Loading KeyKeeper...'} />;
  }

  if (error) {
    return <ErrorScreen error={error} onRetry={initialize} />;
  }

  if (authState === 'new_user' && authUser) {
    return <UnauthorizedScreen authUser={authUser} />;
  }

  if (authState === 'pending') {
    return <AccessDeniedScreen />;
  }

  // Show login page if not authenticated
  if (authState === 'not_authenticated') {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Users with "user" role only see their assigned keys
  if (authUser?.role === 'user') {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/logout" element={<Logout />} />
          <Route path="*" element={<MyKeys />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Key requester routes - can request keys, view people and inventory (read-only context), no settings
  if (authUser?.role === 'key-requester') {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Navigate to="/key-requests" replace />} />
          <Route path="/logout" element={<Logout />} />
          <Route
            path="/"
            element={<Navigate to="/key-requests" replace />}
          />
          <Route
            path="/key-requests"
            element={
              <Layout>
                <KeyRequests />
              </Layout>
            }
          />
          <Route
            path="/people"
            element={
              <Layout>
                <People />
              </Layout>
            }
          />
          <Route
            path="/inventory"
            element={
              <Layout>
                <KeyInventory />
              </Layout>
            }
          />
          <Route
            path="/inventory/key-type/:keyTypeId"
            element={
              <Layout>
                <KeyTypeDetail />
              </Layout>
            }
          />
          <Route
            path="/help"
            element={
              <Layout>
                <Help />
              </Layout>
            }
          />
          {/* Redirect everything else to key requests */}
          <Route path="*" element={<Navigate to="/key-requests" replace />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Site-admin routes - no access to settings
  if (authUser?.role === 'site-admin') {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/logout" element={<Logout />} />
          <Route
            path="/"
            element={
              <Layout>
                <Dashboard />
              </Layout>
            }
          />
          <Route
            path="/inventory"
            element={
              <Layout>
                <KeyInventory />
              </Layout>
            }
          />
          <Route
            path="/inventory/key-type/:keyTypeId"
            element={
              <Layout>
                <KeyTypeDetail />
              </Layout>
            }
          />
          <Route
            path="/people"
            element={
              <Layout>
                <People />
              </Layout>
            }
          />
          <Route
            path="/key-requests"
            element={
              <Layout>
                <KeyRequests />
              </Layout>
            }
          />
          <Route path="/export" element={<Navigate to="/settings/backup" replace />} />
          <Route
            path="/visitors"
            element={
              <Layout>
                <Visitors />
              </Layout>
            }
          />
          <Route
            path="/help"
            element={
              <Layout>
                <Help />
              </Layout>
            }
          />
          {/* Redirect settings pages to dashboard for site-admin */}
          <Route path="/settings/*" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Admin routes - full access including settings
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/logout" element={<Logout />} />
        <Route
          path="/"
          element={
            <Layout>
              <Dashboard />
            </Layout>
          }
        />
        <Route
          path="/inventory"
          element={
            <Layout>
              <KeyInventory />
            </Layout>
          }
        />
        <Route
          path="/inventory/key-type/:keyTypeId"
          element={
            <Layout>
              <KeyTypeDetail />
            </Layout>
          }
        />
        <Route
          path="/people"
          element={
            <Layout>
              <People />
            </Layout>
          }
        />
        <Route
          path="/key-requests"
          element={
            <Layout>
              <KeyRequests />
            </Layout>
          }
        />
          <Route path="/export" element={<Navigate to="/settings/backup" replace />} />
          <Route
            path="/settings"
          element={
            <Layout>
              <Settings />
            </Layout>
          }
        />
        <Route
          path="/settings/locations"
          element={
            <Layout>
              <Locations />
            </Layout>
          }
        />
        <Route
          path="/settings/keytypes"
          element={
            <Layout>
              <KeyTypes />
            </Layout>
          }
        />
        <Route
          path="/settings/users"
          element={
            <Layout>
              <Users />
            </Layout>
          }
        />
        <Route
          path="/settings/backup"
          element={
            <Layout>
              <BackupRestore />
            </Layout>
          }
        />
        <Route
          path="/settings/notifications"
          element={
            <Layout>
              <NotificationSettings />
            </Layout>
          }
        />
        <Route
          path="/visitors"
          element={
            <Layout>
              <Visitors />
            </Layout>
          }
        />
        <Route
          path="/help"
          element={
            <Layout>
              <Help />
            </Layout>
          }
        />
        {/* Catch-all route for any other paths - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
